import type { ComparedPlayer, ProviderFixtureBundle } from './api';

export type ResolvedMatchDisplayStatus = 'FT' | 'ENDED_STALE' | 'LIVE' | 'HT' | 'UPCOMING';

export interface TeamMatchDisplayState {
  teamId: number;
  status: ResolvedMatchDisplayStatus;
  minute: number | null;
  sourceProvider: string;
  kickoffAt: string | null;
}

export interface PlayerMatchDisplayState {
  status: ResolvedMatchDisplayStatus | 'NO FIXTURE';
  label: string;
  tone: 'finished' | 'live' | 'upcoming' | 'idle';
  sourceProvider: string;
}

const FINISHED = new Set(['FT', 'AET', 'PEN', 'FINISHED', 'FULL_TIME']);
const HALF_TIME = new Set(['HT', 'HALF_TIME']);
const LIVE = new Set(['LIVE', '1H', '2H', 'ET', 'PEN_LIVE', 'IN_PLAY']);

function participantTeamId(id: string): number | null {
  const match = /^fpl-team:(\d+)$/.exec(id);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isInteger(value) && value > 0 ? value : null;
}

function normalizeStatus(bundle: ProviderFixtureBundle): ResolvedMatchDisplayStatus {
  const raw = bundle.fixture.status.trim().toUpperCase();
  if (FINISHED.has(raw)) return 'FT';
  if (raw === 'STALE') return 'ENDED_STALE';
  if (HALF_TIME.has(raw)) return 'HT';
  if (LIVE.has(raw) || bundle.fixture.minute !== null) return 'LIVE';
  return 'UPCOMING';
}

function priority(state: TeamMatchDisplayState): number {
  if (state.status === 'LIVE' || state.status === 'HT') return 4;
  if (state.status === 'UPCOMING') return 3;
  return 2;
}

function kickoffMs(state: TeamMatchDisplayState): number {
  if (!state.kickoffAt) return 0;
  const parsed = Date.parse(state.kickoffAt);
  return Number.isFinite(parsed) ? parsed : 0;
}

function prefer(current: TeamMatchDisplayState | undefined, next: TeamMatchDisplayState): TeamMatchDisplayState {
  if (!current) return next;
  const currentPriority = priority(current);
  const nextPriority = priority(next);
  if (nextPriority !== currentPriority) return nextPriority > currentPriority ? next : current;

  if (next.status === 'LIVE' || next.status === 'HT') {
    return (next.minute ?? -1) >= (current.minute ?? -1) ? next : current;
  }
  if (next.status === 'UPCOMING') {
    const currentKickoff = kickoffMs(current);
    const nextKickoff = kickoffMs(next);
    if (!currentKickoff) return next;
    if (!nextKickoff) return current;
    return nextKickoff < currentKickoff ? next : current;
  }
  return kickoffMs(next) >= kickoffMs(current) ? next : current;
}

export function buildTeamMatchDisplayStates(fixtures: ProviderFixtureBundle[]): Map<number, TeamMatchDisplayState> {
  const states = new Map<number, TeamMatchDisplayState>();
  for (const bundle of fixtures) {
    const status = normalizeStatus(bundle);
    const stateBase = {
      status,
      minute: status === 'LIVE' || status === 'HT' ? bundle.fixture.minute : null,
      sourceProvider: bundle.fixture.sourceProvider,
      kickoffAt: bundle.startingAt,
    };
    for (const participant of bundle.participants) {
      const teamId = participantTeamId(participant.id);
      if (teamId === null) continue;
      const next: TeamMatchDisplayState = { teamId, ...stateBase };
      states.set(teamId, prefer(states.get(teamId), next));
    }
  }
  return states;
}

export function playerMatchDisplayState(player: ComparedPlayer, resolved: TeamMatchDisplayState | undefined, preview = false): PlayerMatchDisplayState {
  if (preview) return fromComparePlayer(player);
  if (resolved) {
    if (resolved.status === 'FT') return { status: 'FT', label: 'MATCH FT', tone: 'finished', sourceProvider: resolved.sourceProvider };
    if (resolved.status === 'ENDED_STALE') return { status: 'ENDED_STALE', label: 'MATCH ENDED · SOURCE STALE', tone: 'finished', sourceProvider: resolved.sourceProvider };
    if (resolved.status === 'HT') return { status: 'HT', label: 'MATCH HT', tone: 'live', sourceProvider: resolved.sourceProvider };
    if (resolved.status === 'LIVE') return { status: 'LIVE', label: `MATCH LIVE${resolved.minute === null ? '' : ` ${resolved.minute}'`}`, tone: 'live', sourceProvider: resolved.sourceProvider };
    return { status: 'UPCOMING', label: 'MATCH UPCOMING', tone: 'upcoming', sourceProvider: resolved.sourceProvider };
  }
  return fromComparePlayer(player);
}

function fromComparePlayer(player: ComparedPlayer): PlayerMatchDisplayState {
  if (player.fixtureStatus === 'FINISHED') return { status: 'FT', label: 'MATCH FT', tone: 'finished', sourceProvider: 'fpl-compare' };
  if (player.fixtureStatus === 'LIVE') return { status: 'LIVE', label: `MATCH LIVE${player.fixtureMinute === null ? '' : ` ${player.fixtureMinute}'`}`, tone: 'live', sourceProvider: 'fpl-compare' };
  if (player.fixtureStatus === 'UPCOMING') return { status: 'UPCOMING', label: 'MATCH UPCOMING', tone: 'upcoming', sourceProvider: 'fpl-compare' };
  return { status: 'NO FIXTURE', label: 'NO FIXTURE', tone: 'idle', sourceProvider: 'fpl-compare' };
}
